import { useRef, useState } from 'react';

import BottomSheet from '@/components/BottomSheet';
import { cn } from '@/lib/utils';

/**
 * reaction-emoji-signal — one section of the picker.
 *
 * The sheet no longer knows any catalogue: it renders what it is handed. C3b
 * had unified reactions and circles onto one list ("exactly one list to
 * curate"); the two now have opposite requirements — a circle may be 🦚, a
 * REACTION 🦚 says nothing about how a track landed — so the SOURCES diverge
 * while the sheet stays shared.
 */
export interface PickerSection {
  id: string;
  /** Shown in the jump strip. */
  icon: string;
  /** Already translated by the caller — the sheet holds no i18n. */
  label: string;
  emojis: readonly string[];
}

/**
 * Bottom-sheet emoji picker — the single place an emoji is chosen in the app.
 *
 * C3 (2.1.0) built it for reactions; C3b (2.1.1) generalised it so a circle
 * picks its emoji from the very same catalogue, in the very same sheet. The
 * full list is far too long for one screen, so the emojis scroll vertically
 * under sticky category labels, with a category strip pinned above to jump
 * straight to a section.
 *
 * `testIdPrefix` namespaces the test ids per flavour — `reaction-emoji-😀` for
 * reactions, `circle-emoji-😀` for circles — so each caller keeps its own
 * selectors.
 */
export default function EmojiPickerSheet({
  sections,
  current,
  onPick,
  onClose,
  testIdPrefix,
  title,
}: {
  sections: readonly PickerSection[];
  current: string | null;
  onPick: (emoji: string) => void;
  onClose: () => void;
  testIdPrefix: string;
  title?: string;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [activeCategory, setActiveCategory] = useState<string>(sections[0]?.id ?? '');

  /**
   * INVARIANT (MR-2.1.0): the scroll container below MUST keep `tw:relative`.
   * `offsetTop` is measured from the nearest *positioned* ancestor, so the
   * arithmetic here is only correct while that ancestor is the scroller itself.
   * Drop the class and offsetParent walks up to the `tw:fixed` overlay, the
   * grabber/title/strip heights leak into every offsetTop, and both the jump
   * and the active-category highlight land off by that amount.
   */
  const jumpToCategory = (id: string) => {
    const container = scrollRef.current;
    const section = sectionRefs.current[id];
    if (container && section) container.scrollTop = section.offsetTop;
    setActiveCategory(id);
  };

  /** Keeps the strip in sync while the user scrolls the grid by hand. */
  const handleScroll = () => {
    const container = scrollRef.current;
    if (!container) return;
    let visible: string = sections[0]?.id ?? '';
    for (const group of sections) {
      const section = sectionRefs.current[group.id];
      if (section && section.offsetTop <= container.scrollTop + 8) visible = group.id;
    }
    setActiveCategory(visible);
  };

  return (
    // emoji-sheet-swipe — a BottomSheet: swipe down with the grid at the top
    // closes it (the grid, not the sheet, is what scrolls — BottomSheet reads
    // the list under the finger), and the screen below never sees the pull.
    <BottomSheet open onClose={onClose} testid={`${testIdPrefix}-picker`}>
      {title && (
        <h2 className="tw:mb-3 tw:text-center tw:text-[15px] tw:font-semibold tw:text-white">
          {title}
        </h2>
      )}

      {/* Jump strip: one tab per category, scrolls horizontally on small screens. */}
      <div
        className="tw:mb-2 tw:flex tw:gap-1 tw:overflow-x-auto tw:pb-2"
        data-testid={`${testIdPrefix}-category-strip`}
      >
        {sections.map((group) => (
          <button
            key={group.id}
            type="button"
            aria-label={group.label}
            aria-current={activeCategory === group.id}
            data-testid={`${testIdPrefix}-category-${group.id}`}
            onClick={() => jumpToCategory(group.id)}
            className={cn(
              'tw:flex tw:size-9 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:text-xl tw:active:scale-90',
              activeCategory === group.id ? 'tw:bg-blue-primary/30' : 'tw:bg-dark-primary',
            )}
          >
            {group.icon}
          </button>
        ))}
      </div>

      <div
        ref={scrollRef}
        onScroll={handleScroll}
        // `tw:relative` is load-bearing — see the INVARIANT note on jumpToCategory.
        className="tw:relative tw:h-[46vh] tw:overflow-y-auto tw:overscroll-contain"
        data-testid={`${testIdPrefix}-scroll-area`}
      >
        {sections.map((group) => (
          <div
            key={group.id}
            ref={(el) => {
              sectionRefs.current[group.id] = el;
            }}
            data-testid={`${testIdPrefix}-section-${group.id}`}
          >
            <h3 className="tw:sticky tw:top-0 tw:z-10 tw:bg-dark-1 tw:py-1 tw:text-xs tw:font-medium tw:uppercase tw:text-grey-4">
              {group.label}
            </h3>
            <div className="tw:grid tw:grid-cols-8 tw:gap-1">
              {group.emojis.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  onClick={() => onPick(emoji)}
                  data-testid={`${testIdPrefix}-emoji-${emoji}`}
                  className={cn(
                    'tw:flex tw:aspect-square tw:items-center tw:justify-center tw:rounded-xl tw:text-[26px] tw:active:scale-90',
                    current === emoji && 'tw:bg-blue-primary/30',
                  )}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </BottomSheet>
  );
}
